import * as Schema from "effect/Schema";

import { NonNegativeInt, ThreadId, TrimmedNonEmptyString } from "./baseSchemas";
import {
  PROVIDER_SEND_TURN_MAX_FILE_BYTES,
  PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
} from "./orchestration";

/**
 * Addresses for the bytes a client cannot fetch by guessing.
 *
 * Forge already serves the bytes: attachments under `/attachments/*` and
 * project favicons at `GET /api/project-favicon?cwd=`. What it did not serve
 * was the question "what is the URL for this thing", which is the only way a
 * client that holds an attachment id or a workspace root can reach either.
 * The phone asks that question on the thread list for every project, so
 * until this existed the answer was an error the phone drew as a spinner.
 *
 * These schemas are the Forge side of `assets.*` in the vendored t3 contracts.
 * They must decode what that package encodes: same tags, same field names.
 */

const ASSET_PATH_MAX_LENGTH = 1024;
const ASSET_ID_MAX_LENGTH = 256;
const ASSET_URL_MAX_LENGTH = 4096;

export const AssetResource = Schema.Union([
  /**
   * A file inside a thread's workspace. Forge has no HTTP route for these:
   * workspace files are read over `projects.readFile`, not addressed by URL.
   * Declared so the request decodes and can be refused with a reason.
   */
  Schema.TaggedStruct("workspace-file", {
    threadId: ThreadId,
    path: TrimmedNonEmptyString.check(Schema.isMaxLength(ASSET_PATH_MAX_LENGTH)),
  }),
  Schema.TaggedStruct("attachment", {
    attachmentId: TrimmedNonEmptyString.check(Schema.isMaxLength(ASSET_ID_MAX_LENGTH)),
  }),
  Schema.TaggedStruct("project-favicon", {
    cwd: TrimmedNonEmptyString.check(Schema.isMaxLength(ASSET_PATH_MAX_LENGTH)),
    /**
     * A cache-key hint only. The favicon route resolves the real path from the
     * project itself, so a client that guesses wrong gets the right image.
     */
    path: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(ASSET_PATH_MAX_LENGTH))),
  }),
]);
export type AssetResource = typeof AssetResource.Type;

export const AssetCreateUrlInput = Schema.Struct({
  resource: AssetResource,
});
export type AssetCreateUrlInput = typeof AssetCreateUrlInput.Type;

export const AssetCreateUrlResult = Schema.Struct({
  /**
   * Relative on purpose. The client resolves it against the base URL it is
   * already talking to, so the same answer works over a tunnel, a relay and
   * a loopback port without the server having to know which one it is on.
   */
  relativeUrl: TrimmedNonEmptyString.check(Schema.isMaxLength(ASSET_URL_MAX_LENGTH)),
  /** Epoch milliseconds. After this the URL stops authenticating and must be re-asked. */
  expiresAt: Schema.Number,
  sourcePath: Schema.optional(
    TrimmedNonEmptyString.check(Schema.isMaxLength(ASSET_PATH_MAX_LENGTH)),
  ),
});
export type AssetCreateUrlResult = typeof AssetCreateUrlResult.Type;

export const ATTACHMENT_UPLOAD_URL_TTL_MS = 10 * 60_000;

const ImageAttachmentCreateUploadUrlInput = Schema.Struct({
  type: Schema.optionalKey(Schema.Literal("image")),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES),
  ),
});

const FileAttachmentCreateUploadUrlInput = Schema.Struct({
  type: Schema.Literal("file"),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100)),
  sizeBytes: NonNegativeInt.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_FILE_BYTES),
  ),
});

export const AttachmentCreateUploadUrlInput = Schema.Union([
  ImageAttachmentCreateUploadUrlInput,
  FileAttachmentCreateUploadUrlInput,
]);
export type AttachmentCreateUploadUrlInput = typeof AttachmentCreateUploadUrlInput.Type;

export const AttachmentCreateUploadUrlResult = Schema.Struct({
  attachmentId: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  relativeUrl: TrimmedNonEmptyString.check(Schema.isMaxLength(4096)),
  expiresAt: Schema.Number,
});
export type AttachmentCreateUploadUrlResult = typeof AttachmentCreateUploadUrlResult.Type;

export const AttachmentDeleteInput = Schema.Struct({
  attachmentId: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
});
export type AttachmentDeleteInput = typeof AttachmentDeleteInput.Type;

export class AttachmentUploadSigningKeyError extends Schema.TaggedErrorClass<AttachmentUploadSigningKeyError>()(
  "AttachmentUploadSigningKeyError",
  {
    message: Schema.Literal("Failed to load the attachment upload signing key."),
    cause: Schema.optional(Schema.Defect),
  },
) {}

/**
 * The resource cannot be addressed by URL on this server.
 *
 * Tag and message match the vendored client's `AssetAccessError` union so the
 * phone decodes the failure rather than falling through to a generic transport
 * error. `workspace-file` is the case that lands here.
 */
export class AssetWorkspaceResolutionError extends Schema.TaggedErrorClass<AssetWorkspaceResolutionError>()(
  "AssetWorkspaceResolutionError",
  {
    resource: AssetResource,
    cause: Schema.optional(Schema.Defect),
  },
) {
  // fallow-ignore-next-line unused-class-member
  override get message(): string {
    return "Failed to resolve workspace.";
  }
}

/** The signing key that makes an asset URL self-authenticating could not be read. */
export class AssetSigningKeyLoadError extends Schema.TaggedErrorClass<AssetSigningKeyLoadError>()(
  "AssetSigningKeyLoadError",
  {
    resource: AssetResource,
    cause: Schema.optional(Schema.Defect),
  },
) {
  // fallow-ignore-next-line unused-class-member
  override get message(): string {
    return "Failed to load the asset signing key.";
  }
}

export const AssetAccessError = Schema.Union([
  AssetWorkspaceResolutionError,
  AssetSigningKeyLoadError,
]);
export type AssetAccessError = typeof AssetAccessError.Type;

export const AssetsRpcSchemas = {
  createUrl: {
    input: AssetCreateUrlInput,
    output: AssetCreateUrlResult,
  },
} as const;
